/**
 * The fixed chrome at the top of every default-layout page: the announcement
 * bar, when the page has one, over the header.
 *
 * CSS reads its height from `--chrome-h` (with `--announcement-h` and
 * `--header-h`), defined in the `:root` preflight of `uno.config.ts` from the
 * figures below. This module is the same arithmetic for the few places that
 * need the number in script — an observer's `rootMargin`, a scroll target, a
 * sticky `top` written inline. They cannot read the variable instead:
 * `--chrome-h` holds `calc(var(--announcement-h) + var(--header-h))`, and an
 * unregistered custom property comes back from `getComputedStyle` as that token
 * string, not as a length.
 *
 * With the bar shown the chrome is 100px on both sides of `lg` (44 + 56 on
 * phones, 36 + 64 above), which is the figure the offsets across the storefront
 * were written against. Without it only the header is left.
 *
 * `uno.config.ts` imports this file when the CSS is built, so it holds plain
 * values and touches the DOM only inside the function — no Nuxt or Vue imports.
 */

/**
 * On `<html>` while the page renders the announcement bar. `AnnouncementBar.vue`
 * derives it from the server-rendered data only, so the offsets never jump
 * after hydration.
 *
 * It goes through `useHead`, and Nuxt holds head changes back during a
 * client-side navigation until `page:finish`, which comes after the new page
 * has mounted. Coming into the default layout from a page without one (Back out
 * of checkout), the class is therefore still missing in `setup` and
 * `onMounted`; it is in place by the next animation frame.
 */
export const ANNOUNCEMENT_BAR_CLASS = 'has-announcement-bar'

/**
 * Where the chrome gives the screen back as the page scrolls
 * (`useMobileChromeAutoHide`): phones, below Uno's `md`. Not up to `lg`, although
 * the header keeps its phone row until there: from `md` the product, outfit and
 * model pages pin a column at a fixed distance under the chrome
 * (`useColumnBalance`, `ModelsProfileHero`), which a header sliding away would
 * leave hanging under an empty band. The pages' sticky add-to-cart bars are
 * phone-only on the same breakpoint.
 */
export const CHROME_AUTO_HIDE_QUERY = '(max-width: 767.98px)'

/**
 * On `<html>` while the header is slid away on a phone: the shopper is
 * scrolling down, and the header stays off until they scroll up.
 * `--chrome-dock-h` drops to 0 under it (see `uno.config.ts`).
 */
export const CHROME_HEADER_HIDDEN_CLASS = 'chrome-header-hidden'

/**
 * Inline custom property on `<html>`: how far the announcement bar has scrolled
 * away on a phone, from 0 up to the bar's height. It moves one-for-one with the
 * page, as the bar would if it were part of it, and the header rides up with
 * it. Unset, so 0, from `md`.
 */
export const CHROME_SHIFT_PROPERTY = '--chrome-shift'

/**
 * How far the page has to travel in one direction before the phone header
 * follows it. Below this a resting thumb or the tail of a fling would flicker
 * the header in and out; the reference storefront brings it back on a ~10px pull.
 */
export const CHROME_SCROLL_TOLERANCE_PX = 6

/**
 * One step of the phone header's hide-on-scroll, as a pure function of where
 * the page is. `lastY` is the reference point: small movements add up against
 * it until they pass the tolerance in one direction, and only a decision (or
 * the forced cases) moves it.
 *
 * - `revealBelow`: at or above this scroll position the header always shows —
 *   the page has not yet scrolled past the whole chrome.
 * - `pinned`: the header must show whatever the direction (focus is inside it).
 */
export function nextChromeHeaderState(input: {
  y: number
  lastY: number
  hidden: boolean
  revealBelow: number
  pinned: boolean
}): { hidden: boolean, lastY: number } {
  const { y, lastY, hidden, revealBelow, pinned } = input
  if (pinned || y <= revealBelow)
    return { hidden: false, lastY: y }
  const delta = y - lastY
  if (delta > CHROME_SCROLL_TOLERANCE_PX)
    return { hidden: true, lastY: y }
  if (delta < -CHROME_SCROLL_TOLERANCE_PX)
    return { hidden: false, lastY: y }
  return { hidden, lastY }
}

/** Where both parts of the chrome change height: Uno's `lg`. */
export const FIXED_CHROME_LG_QUERY = '(min-width: 1024px)'

/**
 * Heights in px below `lg` and from it. The bar's must match
 * `AnnouncementStrip.vue`, the header's the `h-14` / `h-16` rows in `Header.vue`.
 */
export const FIXED_CHROME_HEIGHTS = {
  announcementBar: { base: 44, lg: 36 },
  header: { base: 56, lg: 64 },
} as const

/**
 * Height of the fixed chrome right now, in px.
 *
 * Browser-only, and meant to be called when the number is used rather than
 * captured once: the breakpoint moves with the window (and see
 * `ANNOUNCEMENT_BAR_CLASS` for the frame after a layout change). Nothing
 * rendered on the server may depend on it either, since the server cannot know
 * the viewport — a length that has to be in the HTML belongs in CSS, as
 * `var(--chrome-h)`.
 */
export function fixedChromeHeight(): number {
  const tier = window.matchMedia(FIXED_CHROME_LG_QUERY).matches ? 'lg' : 'base'
  const bar = document.documentElement.classList.contains(ANNOUNCEMENT_BAR_CLASS)
    ? FIXED_CHROME_HEIGHTS.announcementBar[tier]
    : 0
  return bar + FIXED_CHROME_HEIGHTS.header[tier]
}
